import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, TrendingUp, Users, BarChart3,
  ArrowRight, Star, CalendarCheck, UserCheck, Receipt, Clock,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function SalonDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({
    todayRevenue: 0, todayClients: 0,
    walkIns: 0, returningClients: 0,
    totalClients: 0, loyalClients: 0,
    activeStaff: 0, monthRevenue: 0,
  });
  const [services, setServices] = useState<any[]>([]);
  const [todayBookings, setTodayBookings] = useState<any[]>([]);
  const [recentClients, setRecentClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) => new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const currentMonth = today.slice(0, 7);
        const [salesRes, productsRes, customersRes, staffRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/customers`, { credentials: "include" }),
          fetch(`${BASE}/api/staff${branchQuery}`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];
        const allCustomers = customersRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(customersRes.value) : [];
        const branchCustomerIds = new Set((sales as any[]).map((s: any) => s.customerId).filter(Boolean));
        const customers = activeBranchId
          ? (allCustomers as any[]).filter((c: any) => branchCustomerIds.has(c.id))
          : allCustomers;
        const staff = staffRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(staffRes.value) : [];

        const newestSales = sortSalesNewestFirst(sales);
        const todaySales = newestSales.filter((s: any) => lagosDate(s.createdAt) === today);
        const monthSales = newestSales.filter((s: any) => lagosDate(s.createdAt).startsWith(currentMonth));
        const loyalClients = (customers as any[]).filter((c: any) => (c.loyaltyPoints || 0) > 0);
        const walkIns = todaySales.filter((s: any) => !s.customerName || s.customerName === "Walk-in Customer").length;

        setStats({
          todayRevenue: todaySales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          todayClients: todaySales.length,
          walkIns,
          returningClients: todaySales.length - walkIns,
          totalClients: (customers as any[]).length,
          loyalClients: loyalClients.length,
          activeStaff: (staff as any[]).filter((s: any) => s.isActive).length,
          monthRevenue: monthSales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
        });

        // Sort services by price descending (most premium first)
        const activeServices = products as any[];
        setServices(activeServices.sort((a: any, b: any) => Number(b.price) - Number(a.price)).slice(0, 6));
        setTodayBookings(todaySales.slice(0, 6));
        setRecentClients((customers as any[]).filter((c: any) => c.loyaltyPoints > 0).sort((a: any, b: any) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()).slice(0, 4));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">✂️</span>
            <h1 className="text-2xl font-bold">{store?.name || "My Salon"}</h1>
            <Badge className="bg-pink-100 text-pink-700 border-pink-200">Salon & Beauty</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Beauty & Services Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          <Link href="/customers"><Button variant="outline" size="sm" className="gap-1.5"><UserCheck className="w-3.5 h-3.5" />Clients</Button></Link>
          <Link href="/sales/new"><Button size="sm" className="gap-1.5 bg-pink-600 hover:bg-pink-700"><Sparkles className="w-3.5 h-3.5" />New Service</Button></Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Today's Revenue"
          value={maskMoney(formatCurrency(stats.todayRevenue))}
          icon={TrendingUp}
          color="bg-pink-100 text-pink-600"
          sub={`${stats.todayClients} client${stats.todayClients !== 1 ? "s" : ""} served`}
        />
        <StatCard
          label="This Month"
          value={maskMoney(formatCurrency(stats.monthRevenue))}
          icon={BarChart3}
          color="bg-fuchsia-100 text-fuchsia-600"
        />
        <StatCard
          label="Loyal Clients"
          value={String(stats.loyalClients)}
          icon={Star}
          color="bg-amber-100 text-amber-600"
          sub={`of ${stats.totalClients} total clients`}
        />
        <StatCard
          label="Stylists on Duty"
          value={String(stats.activeStaff)}
          icon={Clock}
          color="bg-emerald-100 text-emerald-600"
          sub="active staff members"
        />
      </div>

      {/* Today's client breakdown */}
      {stats.todayClients > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-pink-700">{stats.walkIns}</div>
              <div className="text-xs text-pink-600 font-medium">Walk-in Clients Today</div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-purple-700">{stats.returningClients}</div>
              <div className="text-xs text-purple-600 font-medium">Returning / Named Clients</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "New Service",      href: "/sales/new",         icon: Sparkles,      color: "bg-pink-50 border-pink-200 hover:bg-pink-100"     },
            { label: "Client Walk-in",   href: "/customers",         icon: UserCheck,     color: "bg-purple-50 border-purple-200 hover:bg-purple-100"},
            { label: "Staff Rota",       href: "/attendance",        icon: Clock,         color: "bg-blue-50 border-blue-200 hover:bg-blue-100",    mgr: true },
            { label: "Loyalty Rewards",  href: "/people/loyalty",    icon: Star,          color: "bg-amber-50 border-amber-200 hover:bg-amber-100"  },
          ].filter((a: any) => !a.mgr || isManager).map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Service Menu — sorted by price */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />Service Menu
            </h3>
            <Link href="/inventory"><span className="text-xs text-primary hover:underline cursor-pointer">Edit services →</span></Link>
          </div>
          {services.length === 0 ? (
            <div className="text-center py-6">
              <Sparkles className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No services added yet</p>
              <Link href="/inventory?add=1"><Button size="sm" className="mt-3 bg-pink-600 hover:bg-pink-700">Add First Service</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {services.map((svc: any, idx: number) => (
                <div key={svc.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                      ${idx === 0 ? "bg-amber-100 text-amber-700" : idx === 1 ? "bg-slate-100 text-slate-600" : "bg-pink-50 text-pink-500"}`}>
                      {idx + 1}
                    </div>
                    <span className="text-sm font-medium">{svc.name}</span>
                  </div>
                  <span className="text-sm font-bold text-pink-600">{formatCurrency(Number(svc.price))}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Bookings / Services */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-purple-500" />Today's Services
            </h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">View all →</span></Link>
          </div>
          {todayBookings.length === 0 ? (
            <div className="text-center py-8">
              <CalendarCheck className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No services recorded today yet</p>
              <Link href="/sales/new"><Button size="sm" className="mt-3 bg-pink-600 hover:bg-pink-700">Record First Service</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {todayBookings.map((s: any) => {
                const isWalkIn = !s.customerName || s.customerName === "Walk-in Customer";
                return (
                  <div key={s.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{s.customerName ?? "Walk-in Client"}</span>
                        {isWalkIn && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-500">Walk-in</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {s.receiptNumber} · {new Date(s.createdAt).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-pink-600">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Loyalty clients */}
      {isManager && recentClients.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" />Top Loyalty Clients</h3>
            <Link href="/people/loyalty"><span className="text-xs text-primary hover:underline cursor-pointer">Manage loyalty →</span></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {recentClients.map((c: any) => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-bold text-sm flex-shrink-0">
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-amber-600 font-medium flex items-center gap-0.5">
                    <Star className="w-3 h-3" />{c.loyaltyPoints} pts
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-pink-500" />Owner Analytics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss",       href: "/accounting/profit-loss" },
              { label: "Client Reports",       href: "/people/customer-reports" },
              { label: "Loyalty & Rewards",    href: "/people/loyalty" },
              { label: "Staff Performance",    href: "/people/staff-performance" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
